const FORMAT_BY_EXTENSION = {
  jpg: "JPEG",
  jpeg: "JPEG",
  jpe: "JPEG",
  png: "PNG",
  webp: "WebP",
  avif: "AVIF",
  bmp: "BMP",
  dib: "BMP",
  heic: "HEIC",
  heif: "HEIF",
  hif: "HEIF",
  tif: "TIFF",
  tiff: "TIFF",
};

const MIME_BY_FORMAT = {
  JPEG: ["image/jpeg", "image/jpg"],
  PNG: ["image/png"],
  WebP: ["image/webp"],
  AVIF: ["image/avif"],
  BMP: ["image/bmp", "image/x-ms-bmp"],
  HEIC: ["image/heic", "image/heic-sequence"],
  HEIF: ["image/heif", "image/heif-sequence"],
  TIFF: ["image/tiff", "image/x-tiff"],
};

export const IMAGE_ACCEPT = [
  ".jpg", ".jpeg", ".jpe", ".png", ".webp", ".avif", ".bmp", ".dib",
  ".heic", ".heif", ".hif", ".tif", ".tiff",
  ...Object.values(MIME_BY_FORMAT).flat(),
].join(",");

export const FORMAT_LABEL = "JPEG、PNG、WebP、HEIC/HEIF、AVIF、TIFF、BMP";

export function detectFormat(file) {
  const mime = (file?.type || "").toLowerCase();
  for (const [format, types] of Object.entries(MIME_BY_FORMAT)) {
    if (types.includes(mime)) return format;
  }
  const extension = (file?.name || "").split(".").pop()?.toLowerCase();
  return FORMAT_BY_EXTENSION[extension] || null;
}

export function isSupportedImage(file) {
  return detectFormat(file) !== null;
}

export async function decodeImageFile(file) {
  const format = detectFormat(file);
  if (!format) throw new Error(`不支持“${file?.name || "该文件"}”的图片格式。`);

  let bitmap;
  let decoder = "浏览器原生";
  if (format === "HEIC" || format === "HEIF") {
    bitmap = await decodeHeic(file);
    decoder = "HEIF 解码器";
  } else if (format === "TIFF") {
    bitmap = await decodeTiff(file);
    decoder = "TIFF 解码器";
  } else {
    try {
      bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch (error) {
      if (format === "AVIF") throw new Error("当前浏览器无法解码这张 AVIF 图片，请使用最新版 Chrome、Edge 或 Safari。", { cause: error });
      throw new Error(`无法解码“${file.name}”，文件可能损坏或使用了不受支持的编码。`, { cause: error });
    }
  }

  const preview = await createPreview(bitmap);
  return {
    file,
    bitmap,
    name: file.name,
    width: bitmap.width,
    height: bitmap.height,
    url: preview.url,
    previewBlob: preview.blob,
    format,
    decoder,
  };
}

async function decodeHeic(file) {
  try {
    const { heicTo } = await import("heic-to/csp");
    return await heicTo({ blob: file, type: "bitmap", options: { imageOrientation: "from-image" } });
  } catch (error) {
    throw new Error(`无法解码“${file.name}”。这张 HEIC/HEIF 可能使用了暂不支持的编码，或设备内存不足。`, { cause: error });
  }
}

async function decodeTiff(file) {
  try {
    const UTIF = (await import("utif")).default;
    const buffer = await file.arrayBuffer();
    const ifds = UTIF.decode(buffer);
    const page = ifds.find((item) => Number(item.width || item.t256?.[0]) > 0 && Number(item.height || item.t257?.[0]) > 0) || ifds[0];
    if (!page) throw new Error("TIFF 中没有可显示的图像页。");
    UTIF.decodeImage(buffer, page);
    const rgba = UTIF.toRGBA8(page);
    const imageData = new ImageData(new Uint8ClampedArray(rgba.buffer, rgba.byteOffset, rgba.byteLength), page.width, page.height);
    return await createImageBitmap(imageData);
  } catch (error) {
    throw new Error(`无法解码“${file.name}”。暂不支持部分 RAW、16/32 位或特殊压缩 TIFF。`, { cause: error });
  }
}

async function createPreview(bitmap) {
  const maxSide = 1400;
  const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise((resolve, reject) => canvas.toBlob((value) => value ? resolve(value) : reject(new Error("预览图生成失败。")), "image/jpeg", 0.82));
  return { blob, url: URL.createObjectURL(blob) };
}
